import React, { useMemo } from "react";
import { Badge } from "@shared/ui/badge";
import { Mail, MessageSquare, Clock } from "lucide-react";

const ChannelList = ({ workflowDetail }: { workflowDetail: any }) => {
  // Check if sequencing is enabled
  const enableSequencing = workflowDetail?.result?.enableSequencing || false;

  // Get message sequence data for sequencing mode
  const messageSequence = useMemo(() => {
    if (enableSequencing && workflowDetail?.result?.messageSequence) {
      return workflowDetail.result.messageSequence
        .sort((a: any, b: any) => a.sequenceOrder - b.sequenceOrder)
        .map((item: any) => ({
          channel: item.channelName?.toLowerCase() === "email" ? "email" : "sms",
          channelName: item.channelName,
          templateName: item.templateName,
          waitDuration: item.waitDuration,
          waitUnit: item.waitUnit,
          sequenceOrder: item.sequenceOrder,
        }));
    }
    return [];
  }, [workflowDetail, enableSequencing]);

  // Get communication channels for non-sequencing mode
  const communicationChannels = useMemo(() => {
    if (!enableSequencing) {
      // Check if templates array exists
      if (
        workflowDetail?.result?.templates &&
        workflowDetail.result.templates.length > 0
      ) {
        return workflowDetail.result.templates.map((item: any) => ({
          channel:
            item.channelDisplay?.toLowerCase() === "email" ? "email" : "sms",
          channelName: item.channelDisplay || item.channel_display,
          templateName: item.templateName || item.template_name,
        }));
      }

      // Fallback to communication_channels array
      return (
        workflowDetail?.result?.communicationChannels?.map((item: any) => ({
          channel: item.channel?.toLowerCase() === "email" ? "email" : "sms",
          channelName: item.channel,
          templateName: item.templateName,
        })) || []
      );
    }
    return [];
  }, [workflowDetail, enableSequencing]);

  // Group channels by type for non-sequencing mode
  const groupedChannels = useMemo(() => {
    return communicationChannels.reduce((acc, channel) => {
      if (!acc[channel.channel]) {
        acc[channel.channel] = [];
      }
      acc[channel.channel].push(channel);
      return acc;
    }, {} as Record<string, typeof communicationChannels>);
  }, [communicationChannels]);

  const getChannelIcon = (channelType: string) => {
    if (channelType === "email") {
      return <Mail className="h-5 w-5 text-blue-600" />;
    }
    return <MessageSquare className="h-5 w-5 text-green-600" />;
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            <h3 className="text-base font-semibold text-gray-900">
              Communication Channels
            </h3>
          </div>
          {enableSequencing && messageSequence.length > 0 && (
            <div className="flex items-center gap-2">
              <Badge
                variant="secondary"
                className="bg-blue-100 text-blue-700"
              >
                Sequence Enabled
              </Badge>
              <span className="text-sm text-gray-500">
                {messageSequence.length} steps configured
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="p-6 bg-white">
        {enableSequencing ? (
          // Sequencing Mode UI - Step-wise display
          <div className="space-y-0">
            {messageSequence.map((item, index) => (
              <div key={index} className="relative">
                {/* Step Card */}
                <div className="flex items-start gap-4">
                  {/* Channel Icon */}
                  <div
                    className={`${
                      item.channel === "email" ? "bg-blue-100" : "bg-green-100"
                    } rounded-lg p-3 flex-shrink-0`}
                  >
                    {getChannelIcon(item.channel)}
                  </div>

                  {/* Step Content */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-900">
                        {item.channelName} Message
                      </span>
                      <Badge variant="outline" className="text-xs">
                        Step {item.sequenceOrder}
                      </Badge>
                    </div>
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      <p className="text-sm text-gray-900 font-medium">
                        {item.templateName}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Wait Duration - Only show between steps */}
                {index < messageSequence.length - 1 && (
                  <div className="flex items-center gap-4 py-3 pl-3">
                    {/* Vertical Line */}
                    <div className="w-12 flex justify-center">
                      <div className="h-8 w-0.5 bg-gray-300"></div>
                    </div>
                    {/* Wait Info */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>
                        Wait {item.waitDuration || 0}{" "}
                        {item.waitUnit || "minutes"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          // Non-Sequencing Mode UI - 2-column grid
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(groupedChannels).map(([channelType, channels]) => {
              const channelList = channels as Array<{
                channel: "email" | "sms";
                channelName: string;
                templateName: string;
              }>;

              return (
                <div
                  key={channelType}
                  className="border border-gray-200 rounded-lg p-4 bg-blue-50"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`${
                        channelType === "email"
                          ? "bg-blue-500"
                          : "bg-green-500"
                      } rounded-lg p-3 flex-shrink-0`}
                    >
                      {channelType === "email" ? (
                        <Mail className="h-5 w-5 text-white" />
                      ) : (
                        <MessageSquare className="h-5 w-5 text-white" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-gray-900 mb-1">
                        {channelType === "email"
                          ? "Email Channel"
                          : "Text Message Channel"}
                      </h4>
                      <p className="text-sm text-gray-600 mb-3">
                        {channelList.length} template
                        {channelList.length > 1 ? "s" : ""} selected
                      </p>
                      <ul className="space-y-2">
                        {channelList.map((channel, index) => (
                          <li
                            key={index}
                            className="flex items-center gap-2 text-sm text-gray-900"
                          >
                            <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></span>
                            <span className="font-medium">
                              {channel.templateName}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelList;